
import { useState, useEffect } from "react";
import { ElfScene } from "./types";
import { db } from "@/integrations/cloudflare/client";
import { toast } from "sonner";
import { useToolUsageTracker } from "@/utils/toolUsageTracker";

export const useSceneGenerator = (postId?: string) => {
  const [scenes, setScenes] = useState<ElfScene[]>([]);
  const [currentScene, setCurrentScene] = useState<ElfScene | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const { trackToolUsage } = useToolUsageTracker("sceneGenerator", postId);

  // Load scenes from the database
  useEffect(() => {
    const fetchScenes = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await db
          .from("elf_scenes")
          .select("*");

        if (error) {
          console.error("Error fetching scenes:", error);
          toast.error("Failed to load elf scenes");
        } else {
          setScenes(data as ElfScene[]);
        }
      } catch (err) {
        console.error("Error in scene fetch:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScenes();
  }, []);

  // Generate a random scene
  const spinForScene = () => {
    if (scenes.length === 0) {
      toast.error("No scenes available");
      return;
    }

    setIsLoading(true);
    
    // Track usage
    trackToolUsage({
      action: "spin",
    });

    setTimeout(() => {
      // Select a random scene
      const randomIndex = Math.floor(Math.random() * scenes.length);
      setCurrentScene(scenes[randomIndex]);
      setHasSpun(true);
      setIsLoading(false);
    }, 1000); // Add a slight delay for the spinning effect
  };

  // Reset to try again
  const resetGenerator = () => {
    setCurrentScene(null);
    setHasSpun(false);
  };

  return {
    scenes,
    currentScene,
    isLoading,
    hasSpun,
    spinForScene,
    resetGenerator
  };
};
